"use client";

import { AssetBlok } from "@/components/bloks/Asset";
import BlokWrap from "@/components/bloks/BlokWrap";
import { ButtonBlok } from "@/components/bloks/ButtonBlok";
import { Button } from "@/components/ui/Button";
import { useAccountModal } from "@/features/account/AccountModal";
import type { BlokMemberBanner } from "@/storyblok";
import type { StoryblokAsset } from "@/storyblok-base";
import { useLoggedIn } from "@frend-digital/centra/client";
import type { BlokProps } from "@frend-digital/storyblok";
import styles from "./MemberBanner.module.css";

export const MemberBannerBlok = ({ blok, preview }: BlokProps<BlokMemberBanner>) => {
	const { data: loggedIn } = useLoggedIn();
	const [, setOpen] = useAccountModal();

	// `loggedIn` is `undefined` until the auth check resolves. Default to the
	// logged-out entry in that window (matches guests and the pending state
	// alike) and only reveal the logged-in entry once it's confirmed, so the
	// two Storyblok entries never render together on load.
	if (blok.loggedIn === true && loggedIn !== true) return null;
	if (blok.loggedIn === false && loggedIn === true) return null;

	const usps = [
		{ icon: blok.uspShippingIcon, text: blok.uspShipping },
		{ icon: blok.uspPointsIcon, text: blok.uspPoints },
		{ icon: blok.uspOutletIcon, text: blok.uspOutlet },
	].filter((usp): usp is { icon: StoryblokAsset | undefined; text: string } => !!usp.text);

	return (
		<BlokWrap editable={blok} as="section" className={styles.root}>
			<div className={styles.top}>
				{blok.title && <span className={styles.badge}>{blok.title}</span>}
				{blok.heading && <p className={styles.heading}>{blok.heading}</p>}
				{blok.button?.[0] &&
					(blok.loggedIn === false ? (
						<Button onClick={() => setOpen(true, "register")}>{blok.button[0].text}</Button>
					) : (
						<ButtonBlok blok={blok.button[0]} preview={preview} />
					))}
			</div>
			{usps.length > 0 && (
				<>
					<div className={styles.divider} />
					<ul className={styles.usps}>
						{usps.map(({ icon, text }, index) => (
							// biome-ignore lint/suspicious/noArrayIndexKey: fixed, order-only list of USP rows
							<li key={index} className={styles.item}>
								<span className={styles.icon}>
									{icon?.filename && (
										<span className={styles.iconImage}>
											<AssetBlok source={icon} sizes="20px" />
										</span>
									)}
								</span>
								{text}
							</li>
						))}
					</ul>
				</>
			)}
		</BlokWrap>
	);
};
